import { useEffect, useState } from 'react'
import { default as _omit } from 'lodash/omit'
import { Button, Tabs } from 'components'
import Flex from 'components/priority/Flex/Flex'
import Text from 'components/priority/Text/Text'
import {
  DeceasedInfo,
  Obituary,
  ServiceInfo,
  PersonalImages,
  MemorialContributions,
  CasePreferences,
} from './CaseDetailsTabs'
import { useRecoilCallback, useRecoilState, useResetRecoilState } from 'recoil'
import { caseFieldsAtom, caseValuesAtom, caseAtom, hasNewCaseAtom } from 'atoms'
import { useErrorHandler } from 'hooks/utility/useErrorHandler'
import { useSetToast } from 'hooks'
import { ensureDefaultService } from 'utils/caseDetails/ensureDefaultService'
import { flushAdditionalFamilyDraft } from 'utils/caseDetails/flushAdditionalFamilyDraft'
import { useTranslation } from 'react-i18next'
import { updateCase } from '@/api/cases'
import { useLoadCaseValues } from '@/hooks/utility/useLoadCaseValues'
import { nameOfDeceasedFromCase } from '@/utils/editor-text-box/editorTextBoxAdapter'

export const CaseDetails = ({ updateTable }) => {
  const { t } = useTranslation()
  const setToast = useSetToast()
  const handleError = useErrorHandler('CaseDetails')
  const [activeTab, setActiveTab] = useState(() =>
    t('caseDetails.tabs.deceasedInfo.title'),
  )
  const [pairs, setPairs] = useState([])
  const [_case, setCase] = useRecoilState(caseAtom)
  const resetHasNewCase = useResetRecoilState(hasNewCaseAtom)

  const loadInitialData = useLoadCaseValues()

  useEffect(() => {
    loadInitialData(pairs)

    //eslint-disable-next-line
  }, [pairs])

  const mapValuePairs = (_case) => {
    //map the case object into an array of object for each key/value
    const mapped = _case
      ? Object.keys(_case).map((key) => ({
          id: key,
          value: _case[key],
        }))
      : []
    setPairs(mapped)
  }

  useEffect(() => {
    const caseTmp = { ..._case }

    if (!caseTmp?.services) {
      caseTmp.services = []
    }
    if (caseTmp.id) {
      mapValuePairs(caseTmp)
    }
  }, [_case])

  const saveCase = useRecoilCallback(({ snapshot, set }) => async () => {
    // Recoil 0.5 releases the callback snapshot when the synchronous part of
    // the callback returns — retain it for the whole async save (same
    // pattern as useUpdatePageItems.saveItems).
    const release = snapshot.retain()
    try {
      // Persist any typed-but-not-Added additional-family entry — the payload
      // below is built from the pre-flush snapshot, so it is overridden after.
      const flushedAdditionalFamily = await flushAdditionalFamilyDraft({
        snapshot,
        set,
      })
      //get a snapshot of the recoil state (this is fuzzy to me still)
      //get the ids from the caseFieldsAtom atom
      const ids = await snapshot.getPromise(caseFieldsAtom)
      //create an empty array to store the returned values
      const allValues = []
      //extract the case_id for the patch
      let case_id = ''
      //this code came from the same sample listed above
      //for each id get the caseValue - which is the key/value object
      //push that to the array and save the case_id seperately
      for (const field of ids) {
        const caseValue = await snapshot.getPromise(caseValuesAtom(field))
        if (caseValue.id === 'id') case_id = caseValue.value
        allValues.push(caseValue)
      }
      //map the array of key/value objects back into one flat object
      const mapped = allValues.map((item) => ({ [item.id]: item.value }))
      const caseObj = Object.assign({}, ...mapped)
      //prep to send
      //the server will reject a payload with certain properties, this will remove those and then send the payload.
      const omit = [
        'id',
        'funeral_home_id',
        'created_at',
        'updated_at',
        'archived',
        'filledImages',
        'age',
        'age_years',
        'age_months',
        'age_days',
      ]
      const payload = _omit(caseObj, omit)
      payload.name_of_deceased = nameOfDeceasedFromCase(payload)
      if (flushedAdditionalFamily) {
        payload.additional_family = flushedAdditionalFamily
      }

      if (payload?.services?.length > 0) {
        payload.services = ensureDefaultService(payload.services)
      }

      if (case_id.length > 0) {
        try {
          const { data } = await updateCase(case_id, payload)
          //on response update the pairs which triggers the data load and also update the cases table so if they close out the modal it's up-to-date
          mapValuePairs(data)
          resetHasNewCase()
          if (updateTable) {
            updateTable(false, true)
          }
          setCase(data)
          setToast({ text: t('toast.caseUpdatedSuccessfully') })
        } catch (error) {
          handleError(error?.response?.data?.message || error.message, error)
        }
      }
    } finally {
      release()
    }
  })

  return (
    <Flex width="1056px" height="100vh" column>
      <>
        <Flex
          padding="24px 40px"
          margin="0 0 48px 0"
          height="94px"
          background="paleSecondary"
          align="center"
          justify="space-between"
        >
          <Text size="34px" weight="600">
            {t('caseDetails.caseDetails', {
              name: _case?.name_of_deceased || '',
            })}
          </Text>
          <Button margin="0 25px 0 0" primary onClick={saveCase}>
            {t('caseDetails.saveButton')}
          </Button>
        </Flex>

        <Tabs
          tabs={[
            {
              title: t('caseDetails.tabs.deceasedInfo.title'),
              content: (
                <DeceasedInfo
                  saveCase={saveCase}
                  setActiveTab={setActiveTab}
                  activeTab={activeTab}
                />
              ),
            },
            {
              title: t('caseDetails.tabs.obituary.title'),
              content: (
                <Obituary
                  saveCase={saveCase}
                  caseDetails={_case}
                  setActiveTab={setActiveTab}
                  activeTab={activeTab}
                />
              ),
            },
            {
              title: t('caseDetails.tabs.serviceInfo.title'),
              content: (
                <ServiceInfo
                  saveCase={saveCase}
                  setActiveTab={setActiveTab}
                  activeTab={activeTab}
                />
              ),
            },
            {
              title: t('caseDetails.tabs.imagesAndFamilyMemorials.title'),
              content: (
                <PersonalImages
                  saveCase={saveCase}
                  case_id={_case.id}
                  name_of_deceased={_case.name_of_deceased}
                  setActiveTab={setActiveTab}
                  activeTab={activeTab}
                />
              ),
            },
            {
              title: t('caseDetails.tabs.memorialContributions.title'),
              content: (
                <MemorialContributions
                  saveCase={saveCase}
                  setActiveTab={setActiveTab}
                  activeTab={activeTab}
                  // onFinalSave={onClose}
                />
              ),
            },
            {
              title: t('caseDetails.tabs.casePreferences.title'),
              content: <CasePreferences saveCase={saveCase} />,
            },
          ]}
          selected={[activeTab, setActiveTab]}
        />
      </>
    </Flex>
  )
}

export default CaseDetails
